/**
 * Split-Lanes-By-Provider Default Migration
 *
 * CONTEXT:
 * The calendar day-view "Split lanes by provider" preference
 * (appointments.splitLanesByProvider) originally shipped defaulting to OFF.
 * We've changed the default to ON so the provider swimlanes show by default
 * under the appointment-label lens. Because the preference is persisted
 * per-user, anyone who already opened the calendar has `false` written to their
 * scoped persist storage, and redux-persist restores that stored `false` over
 * the new default — so simply changing the default would not reach existing
 * users.
 *
 * This migration runs ONCE per user, right before the persistor rehydrates, and
 * rewrites a stored `false` (or missing value) to `true` so existing users
 * adopt the new default too.
 *
 * A stored `false` is treated as the untouched old default rather than a
 * deliberate opt-out: the setting has always defaulted OFF, so the only way to
 * reach an explicit off is to toggle it on and then off again. The one-time
 * flag guarantees we only re-default once — afterwards, an explicit off sticks.
 *
 * NOTE: We rewrite localStorage directly (rather than dispatching an action)
 * because it is timing-independent — it changes the source data redux-persist
 * reads at rehydration. The custom `migrate` hook in store/features/persistence.ts
 * is not usable here (redux-persist passes it the target version, so its
 * version loop never runs), which is why the codebase performs one-time data
 * migrations imperatively at store init (see scopedPersistMigration.ts).
 *
 * CREATED: 2026-07-19
 * REMOVE_AFTER: 2026-09-19 (~60 days after release, once existing sessions have
 *   opened the app at least once)
 *
 * TODO(2026-09-19): Remove this migration.
 * - Delete this file: src/web-app/src/store/migrations/splitLanesDefaultMigration.ts
 * - Remove the import and runSplitLanesDefaultMigration() call from
 *   src/web-app/src/store/index.ts
 */

import * as Sentry from '@sentry/react';

import { getScopedKey, type Owner } from '../owner';

/**
 * Scoped persist key for the appointments slice. Derived from the appointments
 * feature persistence config: `feature_<featureId>_<reducerKey>` →
 * `feature_appointments_appointments` (see store/features/persistence.ts).
 */
const APPOINTMENTS_PERSIST_SLICE_KEY = 'feature_appointments_appointments';

/**
 * Per-user, one-time guard. Intentionally NOT prefixed with "persist:" so it is
 * never mistaken for a persisted slice by the scoped-persist key discovery.
 */
function getMigrationFlagKey(owner: Owner): string {
  return `splitLanesDefault_migrated:${owner.userId}`;
}

/**
 * Flip a persisted `splitLanesByProvider: false` (or absent value) to `true`
 * once per user so the new default-on behavior reaches existing users.
 *
 * MUST run BEFORE `persistStore()` rehydrates — it rewrites the stored slice
 * that redux-persist reads, so timing relative to rehydration is not a concern.
 *
 * @param owner - Current authenticated user's owner context
 */
export function runSplitLanesDefaultMigration(owner: Owner): void {
  const flagKey = getMigrationFlagKey(owner);

  try {
    // Idempotent: only migrate once per user.
    if (localStorage.getItem(flagKey) === 'true') {
      return;
    }

    const scopedKey = getScopedKey(owner, APPOINTMENTS_PERSIST_SLICE_KEY);
    const raw = localStorage.getItem(scopedKey);

    // Nothing persisted yet (e.g. the calendar was never opened) — the new
    // default (true) already applies via initialState. Just mark done.
    if (!raw) {
      localStorage.setItem(flagKey, 'true');
      return;
    }

    // redux-persist stores the slice as an object whose field values are each
    // JSON-encoded, e.g. { "splitLanesByProvider": "false", ... }.
    const parsed = JSON.parse(raw) as Record<string, string>;

    // An explicit "true" is already default-on — leave it untouched.
    if (parsed.splitLanesByProvider !== 'true') {
      parsed.splitLanesByProvider = JSON.stringify(true); // → "true"
      localStorage.setItem(scopedKey, JSON.stringify(parsed));

      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log(
          '✅ [Migration] Defaulted splitLanesByProvider ON for existing user',
        );
      }
    }

    localStorage.setItem(flagKey, 'true');
  } catch (error) {
    console.error(
      '[Migration] Error during splitLanesByProvider default migration:',
      error,
    );

    Sentry.captureException(error, {
      tags: { migration: 'split-lanes-default' },
    });

    // Mark as complete even on error to avoid retry loops.
    try {
      localStorage.setItem(flagKey, 'true');
    } catch {
      // localStorage unavailable — nothing more we can do.
    }
  }
}
